import React, { useState, useEffect, useMemo } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { Clock, ChevronRight, History, RotateCcw, Loader2, ArrowLeft, Plus, Minus, Pencil } from 'lucide-react';
import { routinesApi } from '../api';
import { getExerciseInputMode } from '../lib/exerciseDisplay';

// ── Diff helpers ──────────────────────────────────────────────────────────────
// Format a single planned set for display: "[type] min–max · Wkg" (or minutes for cardio).
function formatSet(set, mode) {
  const type = set.set_type && set.set_type !== 'work' ? set.set_type : '';
  if (mode === 'cardio') {
    const min = set.target_duration_seconds ? Math.round(set.target_duration_seconds / 60) : null;
    return [type, min != null ? `${min} min` : ''].filter(Boolean).join(' · ');
  }
  const reps = (set.target_reps_min != null || set.target_reps_max != null)
    ? `${set.target_reps_min ?? ''}${set.target_reps_max != null && set.target_reps_max !== set.target_reps_min ? `–${set.target_reps_max}` : ''}`
    : '';
  const w = set.target_weight || set.weight;
  return [type, reps, w ? `${w}kg` : ''].filter(Boolean).join(' · ');
}

// Stable signature of an exercise's sets, for detecting "changed" (ignores ids).
function exSetsSig(ex) {
  return (ex.sets || []).map(s => `${s.set_type}|${s.target_reps_min}|${s.target_reps_max}|${s.target_weight ?? s.weight ?? ''}|${s.target_duration_seconds ?? ''}`).join(';');
}

// Match exercises by name (handling duplicate names by occurrence index).
function keyOf(name, seen) {
  const n = name || '';
  const c = (seen[n] = (seen[n] || 0) + 1);
  return `${n}#${c}`;
}

// Diff a version's day against the current routine's matching day.
// status per exercise: 'removed' (in version, gone now) | 'changed' | 'unchanged';
// plus 'added' rows appended for exercises now present that the version lacked.
function diffDayExercises(versionDay, currentDay) {
  const curMap = new Map();
  if (currentDay) {
    const seen = {};
    for (const ex of (currentDay.exercises || [])) curMap.set(keyOf(ex.name, seen), ex);
  }
  const rows = [];
  const seenV = {};
  const matched = new Set();
  for (const ex of (versionDay.exercises || [])) {
    const k = keyOf(ex.name, seenV);
    const cur = curMap.get(k);
    if (!cur) {
      rows.push({ ex, status: currentDay ? 'removed' : 'unchanged' });
    } else {
      matched.add(k);
      rows.push({ ex, status: exSetsSig(ex) === exSetsSig(cur) ? 'unchanged' : 'changed', current: cur });
    }
  }
  // Exercises present now but not in this version → added since.
  if (currentDay) {
    for (const [k, ex] of curMap.entries()) {
      if (!matched.has(k)) rows.push({ ex, status: 'added' });
    }
  }
  return rows;
}

// Align version days to current days by day_name (fallback to index).
function currentDayFor(versionDay, idx, currentSnap) {
  const days = currentSnap?.days || [];
  const byName = days.find(d => (d.day_name || '') === (versionDay.day_name || ''));
  return byName || days[idx] || null;
}

function formatDate(iso, language) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const locale = language === 'pt' ? 'pt-PT' : 'en-GB';
    return d.toLocaleString(locale, {
      day: '2-digit', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return iso;
  }
}

function summaryToText(t, summary) {
  if (!summary) return t('routines.history.summary.routineEdited', 'Routine edited');
  if (summary.startsWith('dayNumChanged:')) {
    const n = summary.split(':')[1];
    return t('routines.history.summary.dayNumChanged', { n, defaultValue: `Day ${n} changed` });
  }
  if (summary.startsWith('restoredToVersion:')) {
    const n = summary.split(':')[1];
    return t('routines.history.summary.restoredToVersion', { n, defaultValue: `Restored to v${n}` });
  }
  switch (summary) {
    case 'routineEdited':         return t('routines.history.summary.routineEdited', 'Routine edited');
    case 'replacedPreviousRoutine': return t('routines.history.summary.replacedPreviousRoutine', 'Replaced previous routine');
    case 'routineRemoved':        return t('routines.history.summary.routineRemoved', 'Routine removed');
    case 'multipleDaysChanged':   return t('routines.history.summary.multipleDaysChanged', 'Multiple days changed');
    case 'daysAddedOrRemoved':    return t('routines.history.summary.daysAddedOrRemoved', 'Days added or removed');
    case 'routineDeleted':        return t('routines.history.summary.routineDeleted', 'Routine deleted');
    case 'restoredFromTrash':     return t('routines.history.summary.restoredFromTrash', 'Restored from trash');
    default:                       return summary;
  }
}

/**
 * Routine version history dialog. Shown via ?history={routineId}.
 *
 * Props:
 *  - open: boolean
 *  - routineId: string|null
 *  - onClose: () => void
 *  - queryKeysToInvalidate?: array of react-query keys to invalidate after restore
 */
export default function RoutineHistoryDialog({ open, routineId, onClose, queryKeysToInvalidate = [], currentRoutine = null }) {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  // selectedVersion: 'current' (live routine) | number (a stored version) | null
  const [selectedVersion, setSelectedVersion] = useState('current');
  const [confirmRestore, setConfirmRestore] = useState(false);

  const versionsQuery = useQuery({
    queryKey: ['routine-versions', routineId],
    queryFn: async () => {
      const r = await routinesApi.listVersions(routineId);
      return r.data;
    },
    enabled: !!(open && routineId),
  });

  const versionDetailQuery = useQuery({
    queryKey: ['routine-version', routineId, selectedVersion],
    queryFn: async () => {
      const r = await routinesApi.getVersion(routineId, selectedVersion);
      return r.data;
    },
    enabled: !!(open && routineId && typeof selectedVersion === 'number'),
  });

  // Current (live) routine to diff against. Prefer the prop (reflects unsaved edits in
  // an open editor); otherwise fetch by id (works for coach/template routines).
  const currentFetch = useQuery({
    queryKey: ['routine-current', routineId],
    queryFn: async () => {
      const r = await routinesApi.getById(routineId);
      return r.data;
    },
    enabled: !!(open && routineId && !currentRoutine),
    retry: false,
  });
  const current = currentRoutine || currentFetch.data || null;

  // Reset selection to the current entry when dialog closes
  useEffect(() => {
    if (!open) setSelectedVersion('current');
  }, [open]);

  const restoreMutation = useMutation({
    mutationFn: () => routinesApi.restoreVersion(routineId, selectedVersion),
    onSuccess: () => {
      toast.success(t('routines.history.restoredToast', 'Version restored'));
      queryClient.invalidateQueries({ queryKey: ['routines'] });
      queryClient.invalidateQueries({ queryKey: ['personal-routines'] });
      queryClient.invalidateQueries({ queryKey: ['routine-versions', routineId] });
      queryKeysToInvalidate.forEach((k) => queryClient.invalidateQueries({ queryKey: k }));
      setConfirmRestore(false);
      onClose?.();
    },
    onError: (err) => {
      const msg = err?.response?.data?.detail || err?.message || 'Failed to restore';
      toast.error(typeof msg === 'string' ? msg : t('common.error', 'Error'));
    },
  });

  const versions = versionsQuery.data || [];
  const detail = versionDetailQuery.data;

  return (
    <>
      <Sheet open={open} onOpenChange={(v) => !v && onClose?.()}>
        <SheetContent
          side="right"
          className="w-full sm:max-w-3xl p-0 flex flex-col"
          data-testid="routine-history-dialog"
        >
          <SheetHeader className="px-4 py-3 border-b border-border/30 flex-row items-center gap-3 space-y-0 flex-shrink-0">
            <History className="h-5 w-5 text-primary flex-shrink-0" />
            <SheetTitle className="text-base font-bold flex-1 min-w-0 truncate" style={{ fontFamily: 'Barlow Condensed, sans-serif' }}>
              {t('routines.history.title', 'Version history')}
            </SheetTitle>
          </SheetHeader>

          <div className="flex-1 min-h-0 flex flex-col md:flex-row overflow-hidden">
            {/* Versions list */}
            <div className={`md:w-80 md:border-r md:border-border/30 flex flex-col min-h-0 ${selectedVersion != null ? 'hidden md:flex' : 'flex'}`}>
              <ScrollArea className="flex-1 min-h-0">
                {versionsQuery.isLoading && (
                  <div className="flex items-center justify-center py-8 text-muted-foreground">
                    <Loader2 className="h-5 w-5 animate-spin" />
                  </div>
                )}
                <ul className="divide-y divide-border/30">
                  {/* Current (live) routine — always at the top */}
                  {current && (
                    <li>
                      <button
                        type="button"
                        onClick={() => setSelectedVersion('current')}
                        className={`w-full text-left px-4 py-3 hover:bg-muted/40 transition-colors flex items-start gap-3 ${selectedVersion === 'current' ? 'bg-muted/60' : ''}`}
                        data-testid="routine-version-row-current"
                      >
                        <Clock className="h-4 w-4 mt-0.5 text-primary flex-shrink-0" />
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-0.5">
                            <Badge variant="outline" className="h-4 px-1.5 text-[10px] bg-primary/10 text-primary border-primary/40">
                              {t('routines.history.current', 'Current')}
                            </Badge>
                          </div>
                          <p className="text-sm font-medium truncate">{current.name}</p>
                          <p className="text-xs text-muted-foreground truncate">{t('routines.history.currentSubtitle', 'Your routine right now')}</p>
                        </div>
                        <ChevronRight className="h-4 w-4 mt-1 text-muted-foreground flex-shrink-0 hidden md:block" />
                      </button>
                    </li>
                  )}
                  {!versionsQuery.isLoading && versions.length === 0 && (
                    <li className="px-4 py-8 text-center text-sm text-muted-foreground">
                      {t('routines.history.empty', 'No previous versions yet')}
                    </li>
                  )}
                  {versions.map((v) => {
                    const active = selectedVersion === v.version_number;
                    return (
                      <li key={v.version_number}>
                        <button
                          type="button"
                          onClick={() => setSelectedVersion(v.version_number)}
                          className={`w-full text-left px-4 py-3 hover:bg-muted/40 transition-colors flex items-start gap-3 ${active ? 'bg-muted/60' : ''}`}
                          data-testid={`routine-version-row-${v.version_number}`}
                        >
                          <Clock className="h-4 w-4 mt-0.5 text-muted-foreground flex-shrink-0" />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-0.5">
                              <span className="text-xs font-mono text-muted-foreground">v{v.version_number}</span>
                              {v.reason === 'delete' && (
                                <Badge variant="destructive" className="h-4 px-1.5 text-[10px]">
                                  {t('routines.history.deletedBadge', 'deleted')}
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm font-medium truncate">{summaryToText(t, v.change_summary)}</p>
                            <p className="text-xs text-muted-foreground truncate">
                              {formatDate(v.created_at, i18n.language)}
                              {v.author_name ? ` · ${v.author_name}` : ''}
                            </p>
                          </div>
                          <ChevronRight className="h-4 w-4 mt-1 text-muted-foreground flex-shrink-0 hidden md:block" />
                        </button>
                      </li>
                    );
                  })}
                </ul>
              </ScrollArea>
            </div>

            {/* Preview pane */}
            <div className={`flex-1 min-h-0 flex flex-col ${selectedVersion != null ? 'flex' : 'hidden md:flex'}`}>
              {selectedVersion == null && (
                <div className="flex-1 flex items-center justify-center text-sm text-muted-foreground px-6 text-center">
                  {t('routines.history.pickToPreview', 'Pick a version to preview')}
                </div>
              )}
              {selectedVersion != null && (() => {
                const isCurrent = selectedVersion === 'current';
                const previewSnap = isCurrent ? current : detail?.snapshot;
                const diffBase = isCurrent ? null : current; // diff a version against the live routine
                const loading = !isCurrent && versionDetailQuery.isLoading;
                // Days present now but absent from this version → "added" days.
                const extraDays = (!isCurrent && diffBase)
                  ? (diffBase.days || []).filter(cd => !(previewSnap?.days || []).some(vd => (vd.day_name || '') === (cd.day_name || '')))
                  : [];
                return (
                <>
                  <div className="px-4 py-3 border-b border-border/30 flex items-center gap-2 flex-shrink-0">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="md:hidden"
                      onClick={() => setSelectedVersion(null)}
                      tooltip={t('common.back', 'Back')}
                    >
                      <ArrowLeft className="h-4 w-4" />
                    </Button>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-bold truncate">
                        {isCurrent
                          ? `${t('routines.history.current', 'Current')}${current?.name ? ` · ${current.name}` : ''}`
                          : `v${selectedVersion}${detail?.snapshot?.name ? ` · ${detail.snapshot.name}` : ''}`}
                      </p>
                      <p className="text-xs text-muted-foreground truncate">
                        {isCurrent
                          ? t('routines.history.currentSubtitle', 'Your routine right now')
                          : (detail?.created_at ? formatDate(detail.created_at, i18n.language) : '')}
                      </p>
                    </div>
                    {!isCurrent && (
                      <Button
                        onClick={() => setConfirmRestore(true)}
                        disabled={!detail || restoreMutation.isPending}
                        data-testid="routine-version-restore-btn"
                      >
                        <RotateCcw className="h-3.5 w-3.5 mr-1.5" />
                        {t('routines.history.restoreThisVersion', 'Restore this version')}
                      </Button>
                    )}
                  </div>
                  <ScrollArea className="flex-1 min-h-0">
                    {loading && (
                      <div className="flex items-center justify-center py-8 text-muted-foreground">
                        <Loader2 className="h-5 w-5 animate-spin" />
                      </div>
                    )}
                    {previewSnap && (
                      <div className="px-4 py-3 space-y-3">
                        {/* Diff legend when comparing a version to current */}
                        {!isCurrent && diffBase && (
                          <div className="flex flex-wrap items-center gap-3 text-[11px] text-muted-foreground">
                            <span className="flex items-center gap-1"><Minus className="h-3 w-3 text-red-500" />{t('routines.history.diffRemoved', 'removed since')}</span>
                            <span className="flex items-center gap-1"><Plus className="h-3 w-3 text-emerald-500" />{t('routines.history.diffAdded', 'added since')}</span>
                            <span className="flex items-center gap-1"><Pencil className="h-3 w-3 text-amber-500" />{t('routines.history.diffChanged', 'sets changed')}</span>
                          </div>
                        )}
                        {previewSnap.description && (
                          <p className="text-sm text-muted-foreground">{previewSnap.description}</p>
                        )}
                        {[...(previewSnap.days || []).map((day, di) => ({ day, di, added: false })),
                          ...extraDays.map((day, i) => ({ day, di: (previewSnap.days || []).length + i, added: true }))
                        ].map(({ day, di, added }) => {
                          const rows = added
                            ? (day.exercises || []).map(ex => ({ ex, status: 'added' }))
                            : (diffBase ? diffDayExercises(day, currentDayFor(day, di, diffBase)) : (day.exercises || []).map(ex => ({ ex, status: 'unchanged' })));
                          return (
                            <div key={day.id || di} className={`rounded-lg border overflow-hidden ${added ? 'border-emerald-500/40' : 'border-border/40'}`}>
                              <div className="flex items-center gap-2 px-3 py-2 bg-muted/30">
                                <Badge variant="outline" className={`text-xs h-5 w-5 flex items-center justify-center p-0 ${added ? 'bg-emerald-500/10 text-emerald-500 border-emerald-500/40' : 'bg-primary/10 text-primary border-primary/40'}`}>
                                  {di + 1}
                                </Badge>
                                <span className="text-sm font-semibold flex-1 min-w-0 truncate">{day.day_name || `Day ${di + 1}`}</span>
                                {added && <Badge variant="outline" className="h-4 px-1.5 text-[10px] bg-emerald-500/10 text-emerald-500 border-emerald-500/40">{t('routines.history.diffNewDay', 'new')}</Badge>}
                                <span className="text-xs text-muted-foreground flex-shrink-0">
                                  {(day.exercises?.length || 0)} {t('routines.exercises', 'exercises')}
                                </span>
                              </div>
                              <ul className="divide-y divide-border/20">
                                {rows.map(({ ex, status, current: curEx }, ei) => {
                                  const mode = getExerciseInputMode(ex);
                                  const tone = status === 'removed' ? 'bg-red-500/5' : status === 'added' ? 'bg-emerald-500/5' : status === 'changed' ? 'bg-amber-500/5' : '';
                                  const Icon = status === 'removed' ? Minus : status === 'added' ? Plus : status === 'changed' ? Pencil : null;
                                  const iconColor = status === 'removed' ? 'text-red-500' : status === 'added' ? 'text-emerald-500' : 'text-amber-500';
                                  return (
                                    <li key={ex.id || ei} className={`px-3 py-2 ${tone}`}>
                                      <div className="flex items-center gap-2">
                                        {Icon && <Icon className={`h-3.5 w-3.5 flex-shrink-0 ${iconColor}`} />}
                                        <span className={`text-sm truncate flex-1 min-w-0 ${status === 'removed' ? 'line-through text-muted-foreground' : ''}`}>{ex.name}</span>
                                      </div>
                                      {(ex.sets?.length > 0) && (
                                        <div className="flex flex-wrap gap-1 mt-1 pl-5">
                                          {(ex.sets || []).map((s, si) => (
                                            <span key={si} className="text-[11px] px-1.5 py-0.5 rounded bg-muted/60 text-muted-foreground whitespace-nowrap">{formatSet(s, mode) || '—'}</span>
                                          ))}
                                        </div>
                                      )}
                                      {status === 'changed' && curEx && (
                                        <div className="flex flex-wrap gap-1 mt-1 pl-5 items-center">
                                          <span className="text-[10px] uppercase tracking-wide text-emerald-500 mr-0.5">{t('routines.history.diffNow', 'now')}</span>
                                          {(curEx.sets || []).map((s, si) => (
                                            <span key={si} className="text-[11px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-600 whitespace-nowrap">{formatSet(s, mode) || '—'}</span>
                                          ))}
                                        </div>
                                      )}
                                    </li>
                                  );
                                })}
                              </ul>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </ScrollArea>
                </>
                );
              })()}
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <AlertDialog open={confirmRestore} onOpenChange={setConfirmRestore}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('routines.history.restoreConfirmTitle', 'Restore this version?')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('routines.history.restoreConfirmDescription', 'The current version will be saved automatically and can itself be restored.')}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={restoreMutation.isPending}>{t('common.cancel', 'Cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); restoreMutation.mutate(); }}
              disabled={restoreMutation.isPending}
              data-testid="routine-version-restore-confirm"
            >
              {restoreMutation.isPending && <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />}
              {t('routines.history.restoreThisVersion', 'Restore this version')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
